import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, animate, useReducedMotion } from "framer-motion";
import { PhoneOff, Volume2 } from "lucide-react";
import { t, useLang } from "../../lib/i18n";
import { VOICE_FAIL_KEY } from "../VoiceChat";

let _flyId = 0;

// A round under way: a score that goes up then is a penalty (a pose refused, a
// useful discard…). At the end of a round the summary tells the points.
const LIVE_PHASES = new Set(["drawing", "acting"]);

/** A score that counts up, with a pop in rose, when it goes up. */
function ScoreNumber({ value }) {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(value);
  const [bump, setBump] = useState(0);
  const shownRef = useRef(value);
  useEffect(() => {
    if (reduce || value <= shownRef.current) {
      shownRef.current = value;
      setShown(value);
      return undefined;
    }
    setBump((b) => b + 1);
    const controls = animate(shownRef.current, value, {
      duration: 0.5,
      ease: "easeOut",
      onUpdate: (v) => { shownRef.current = v; setShown(Math.round(v)); },
    });
    return () => controls.stop();
  }, [value, reduce]);
  return <span key={bump} className={bump ? "score-bump" : undefined}>{shown}</span>;
}

/** « +50 » popping up in front of a player's name, then flying into their score. */
function PenaltyChip({ amount, getRow, onLand }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const chip = ref.current;
    const row = getRow();
    const name = row?.querySelector("[data-score-name]");
    const score = row?.querySelector("[data-score-value]");
    // The list folded: no row to fly from, the score takes it at once.
    if (!chip || !name || !score) { onLand(); return undefined; }
    // Its layout size: a transform left by a stopped run (development's StrictMode) does not count.
    const c = { width: chip.offsetWidth, height: chip.offsetHeight };
    const n = name.getBoundingClientRect();
    const s = score.getBoundingClientRect();
    const x0 = Math.max(4, n.left - c.width - 2);
    const y0 = n.top + n.height / 2 - c.height / 2;
    const x1 = s.left + s.width / 2 - c.width / 2;
    const y1 = s.top + s.height / 2 - c.height / 2;
    let live = true;
    const controls = animate(
      chip,
      {
        x: [x0, x0, x0, x0, (x0 + x1) / 2, x1],
        y: [y0 + 6, y0, y0, y0, Math.min(y0, y1) - 16, y1],
        scale: [0.3, 1.25, 1, 1, 0.95, 0.5],
        opacity: [0, 1, 1, 1, 1, 0],
      },
      {
        duration: 1.6,
        times: [0, 0.12, 0.2, 0.55, 0.78, 1],
        ease: ["easeOut", "easeOut", "linear", "easeOut", "easeIn"],
      },
    );
    controls.then(() => { if (live) onLand(); });
    return () => { live = false; controls.stop(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Fixed, at the top of the page: nothing clips it (the list hides its overflow).
  return (
    <span
      ref={ref}
      dir="ltr"
      style={{ position: "fixed", left: 0, top: 0, opacity: 0 }}
      className="z-[9990] pointer-events-none select-none rounded-full bg-rose-500 px-1.5 text-[10px] leading-4 text-white tabular-nums whitespace-nowrap shadow-lg shadow-rose-500/50 ring-1 ring-white/40"
      data-testid="scoreboard-penalty-chip"
    >
      +{amount}
    </span>
  );
}

export const ScoreboardOverlay = ({ players, currentPlayerId, phase, voiceSpeaking = {}, voiceLinks = {}, elimScoreY }) => {
  const [expanded, setExpanded] = useState(true);
  const reduceMotion = useReducedMotion();
  useLang();

  // A penalty taken during the round (owner's request): « +50 » pops up in front
  // of the player's name and flies into the score, which only then counts it.
  // `seen` = the scores already shown or flying; a rise found at render is held
  // back at once (`fresh`), so the new score never shows before its chip lands.
  const [flies, setFlies] = useState([]);
  const seen = useRef(null);
  const rowEls = useRef({});
  const live = LIVE_PHASES.has(phase) && !reduceMotion;
  const fresh = {};
  if (seen.current && live) {
    (players || []).forEach((p) => {
      const was = seen.current[p.user_id];
      const now = p.total_score || 0;
      if (was != null && now > was) fresh[p.user_id] = now - was;
    });
  }
  useLayoutEffect(() => {
    const now = {};
    (players || []).forEach((p) => { now[p.user_id] = p.total_score || 0; });
    seen.current = now;
    const added = Object.entries(fresh).map(([userId, amount]) => ({ id: ++_flyId, userId, amount }));
    if (added.length) setFlies((f) => [...f, ...added]);
  }, [players, live]); // eslint-disable-line react-hooks/exhaustive-deps
  const land = useCallback((id) => setFlies((f) => f.filter((x) => x.id !== id)), []);

  const shownScore = (p) =>
    (p.total_score || 0)
    - (fresh[p.user_id] || 0)
    - flies.reduce((sum, f) => sum + (f.userId === p.user_id ? f.amount : 0), 0);

  const sorted = [...(players || [])].sort((a, b) => {
    if (a.eliminated && !b.eliminated) return 1;
    if (!a.eliminated && b.eliminated) return -1;
    return shownScore(b) - shownScore(a);
  });

  const botLabel = useMemo(() => {
    const map = {};
    let n = 1;
    (players || []).forEach((p) => { if (p.bot_difficulty) map[p.user_id] = `B${n++}`; });
    return map;
  }, [players]);

  if (!sorted.length) return null;

  return (
    <div className="select-none shrink-0" data-testid="scoreboard-overlay">
      <div
        className={`rami-glass bg-black/35 backdrop-blur-md border border-white/12 shadow-md shadow-black/30 ring-1 ring-white/8 flex flex-col ${
          expanded ? "rounded-xl" : "rounded-full"
        }`}
      >
        {/* Toggle button — always visible */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className={`flex items-center gap-1 text-white/90 cursor-pointer transition-all ${
            expanded ? "px-2 pt-1.5 pb-1" : "px-1.5 py-1"
          }`}
          aria-label={t("score.title")}
        >
          <img
            src="/reaper-128.png"
            srcSet="/reaper-96.png 1x, /reaper-128.png 2x"
            alt=""
            draggable={false}
            loading="eager"
            decoding="sync"
            style={{ imageRendering: "auto" }}
            className={`object-cover rounded-full ring-1 ring-rose-500/60 flex-shrink-0 ${
              expanded ? "h-5 w-5" : "h-5 w-5"
            }`}
            data-testid="scoreboard-reaper-icon"
          />
          {!expanded && sorted[0] && (
            <span className="flex items-center gap-1 text-[9px] font-bold text-white/70">
              <span className="truncate max-w-[44px]">
                {botLabel[sorted[0].user_id] ?? sorted[0].name?.slice(0, 5)}
              </span>
              <span className="tabular-nums text-amber-300">{shownScore(sorted[0])}</span>
            </span>
          )}
          {expanded && (
            <span className="text-[8px] font-semibold uppercase tracking-wider text-white/60 ml-0.5">
              {t("score.title")}{elimScoreY != null && <span className="text-amber-300 font-normal normal-case tracking-normal">/{elimScoreY}</span>}
            </span>
          )}
        </button>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.ul
              key="list"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden px-2 pb-1.5 space-y-px"
            >
              {sorted.map((p, idx) => (
                <li
                  key={p.user_id}
                  ref={(el) => { rowEls.current[p.user_id] = el; }}
                  className={`flex items-center justify-between gap-2 text-[10px] leading-tight ${
                    p.eliminated
                      ? "opacity-35 line-through text-slate-400"
                      : p.user_id === currentPlayerId
                      ? "text-amber-200 font-bold"
                      : "text-white/85"
                  }`}
                  data-testid={`scoreboard-row-${p.user_id}`}
                >
                  <span className="flex items-center gap-0.5 min-w-0">
                    <span className="text-white/40 font-mono text-[8px] w-2.5 text-right flex-shrink-0">
                      {idx + 1}.
                    </span>
                    <span className="truncate max-w-[44px]" data-score-name>
                      {botLabel[p.user_id] ?? p.name?.slice(0, 5)}
                    </span>
                    {voiceSpeaking[p.user_id] && (
                      <Volume2 className="h-2.5 w-2.5 text-emerald-400 animate-pulse flex-shrink-0" data-testid={`scoreboard-speaking-${p.user_id}`} />
                    )}
                    {voiceLinks[p.user_id] === "connecting" && (
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0"
                        title={t("voice.linkConnecting")}
                        data-testid={`scoreboard-voice-connecting-${p.user_id}`}
                      />
                    )}
                    {VOICE_FAIL_KEY[voiceLinks[p.user_id]] && (
                      <span title={t(VOICE_FAIL_KEY[voiceLinks[p.user_id]], { name: p.name })} className="flex-shrink-0" data-testid={`scoreboard-voice-failed-${p.user_id}`}>
                        <PhoneOff className="h-2.5 w-2.5 text-rose-400" />
                      </span>
                    )}
                  </span>
                  <span className="font-bold tabular-nums flex-shrink-0" data-score-value>
                    <ScoreNumber value={shownScore(p)} />
                  </span>
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
      {flies.length > 0 && createPortal(
        flies.map((f) => (
          <PenaltyChip
            key={f.id}
            amount={f.amount}
            getRow={() => rowEls.current[f.userId]}
            onLand={() => land(f.id)}
          />
        )),
        document.body,
      )}
    </div>
  );
};

export default ScoreboardOverlay;
